(() => {
  "use strict";

  const RUNTIME_KEY = "__guruServicePagesRuntime";
  const existingRuntime = window[RUNTIME_KEY];

  if (existingRuntime?.version === 3) {
    existingRuntime.scan(document);
    return;
  }

  const MAX_SOURCE_CHARACTERS = 250000;
  const MAX_SOURCE_NODES = 5000;
  const MAX_SOURCE_DEPTH = 32;
  const MAX_AUTHORITY_VALUES = 256;
  const MAX_FAQ_ITEMS = 100;
  const MAX_QUERY_LENGTH = 80;
  const MAX_SEARCHABLE_CHARACTERS = 4000;
  const MAX_SUMMARY_CHARACTERS = 300;
  const MAX_DISCLOSURE_CHARACTERS = 20000;
  const MAX_ENHANCED_TABLES = 32;
  const MAX_EXTERNAL_LABEL_CHARACTERS = 300;
  const ROOT_SELECTOR = "[data-guru-service-root]";
  const controllers = new WeakMap();

  const ALLOWED_ELEMENTS = new Set([
    "a",
    "abbr",
    "b",
    "blockquote",
    "br",
    "caption",
    "code",
    "dd",
    "details",
    "div",
    "dl",
    "dt",
    "em",
    "h2",
    "h3",
    "h4",
    "hr",
    "i",
    "li",
    "ol",
    "p",
    "pre",
    "section",
    "span",
    "strong",
    "summary",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
  ]);
  const NON_CONTENT_ELEMENTS = new Set([
    "base",
    "link",
    "meta",
    "script",
    "source",
    "style",
    "template",
    "track",
  ]);
  const INLINE_NEUTRAL_ELEMENTS = new Set(["cite", "kbd", "mark", "q", "s", "samp", "small", "sub", "sup", "time", "var"]);
  const VOID_ELEMENTS = new Set(["br", "hr"]);
  const PHRASING_ELEMENTS = new Set(["a", "abbr", "b", "br", "code", "em", "i", "span", "strong", "u"]);
  const FLOW_ELEMENTS = new Set([
    ...PHRASING_ELEMENTS,
    "blockquote",
    "details",
    "div",
    "dl",
    "h2",
    "h3",
    "h4",
    "hr",
    "ol",
    "p",
    "pre",
    "section",
    "table",
    "ul",
  ]);
  const HIDDEN_CLASS_PATTERN = /^(?:hidden|is-hidden|visually-hidden|sr-only)$/iu;
  const SAFE_AUTHORED_CLASSES = new Set(["guru-service-table-region"]);
  const ENCODED_URL_STRUCTURE_PATTERN = /%(?:23|2e|2f|3f|5c)/iu;
  const SAFE_URL_SUFFIX_PATTERN = /^[A-Za-z0-9\-._~%!$&'()*+,;=:@/?#]*$/u;
  const SAFE_HOST_AUTHORITY_PATTERN = /^[A-Za-z0-9.-]+$/u;
  const SAFE_PUBLIC_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/u;
  const RAW_FRAME_ELEMENT_PATTERN = /<[fF][rR][aA][mM][eE](?:[\t\n\f\r />])/u;
  const RAW_PARSER_CONTAINER_PATTERN = /<[\t\n\f\r ]*\/?[\t\n\f\r ]*(?:[hH][tT][mM][lL]|[hH][eE][aA][dD]|[bB][oO][dD][yY])(?![A-Za-z0-9_-])/u;
  const ENTITY_REFERENCE_AMPERSAND_PATTERN = /&/gu;
  const ENTITY_REFERENCE_PROBE_TOKEN = "GuruServiceEntityReferenceBoundary";
  const HEADING_FORBIDDEN_DESCENDANTS = "blockquote, details, div, dl, h2, h3, h4, hr, ol, p, pre, section, summary, table, ul";
  const SUMMARY_FORBIDDEN_DESCENDANTS = "a, blockquote, details, div, dl, hr, ol, p, pre, section, summary, table, ul";
  const ANCHOR_FORBIDDEN_DESCENDANTS = "a, details, summary, table";
  const ANCHOR_BLOCK_DESCENDANTS = "blockquote, div, dl, h2, h3, h4, hr, ol, p, pre, section, ul";

  const rootsWithin = (scope) => {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    return [...new Set(roots)];
  };

  const hasRawControlOrWhitespace = (value) => /[\p{White_Space}\p{Cc}\p{Cf}]/u.test(value);

  const recursivelyDecodedValues = (value) => {
    const values = [];
    let decoded = value;

    for (let pass = 0; pass < 6; pass += 1) {
      if (/%(?![0-9a-f]{2})/iu.test(decoded)) return null;
      values.push(decoded);
      if (!/%[0-9a-f]{2}/iu.test(decoded)) return values;

      let next;
      try {
        next = decodeURIComponent(decoded);
      } catch (_error) {
        return null;
      }
      if (next === decoded) return values;
      decoded = next;
    }

    return /%[0-9a-f]{2}/iu.test(decoded) ? null : [...values, decoded];
  };

  const hasEncodedControlOrWhitespace = (value) => {
    const decodedValues = recursivelyDecodedValues(value);
    if (!decodedValues) return true;

    return decodedValues.some((decoded) => (
      hasRawControlOrWhitespace(decoded)
      || /&(?:#[xX]?[0-9a-f]+|[a-z][a-z0-9]+);/iu.test(decoded)
    ));
  };

  const hasDotPathSegment = (absoluteValue) => {
    const match = absoluteValue.match(/^https:\/\/[^/?#]+([^?#]*)/iu);
    if (!match) return true;
    return match[1].split("/").some((segment) => segment === "." || segment === "..");
  };

  const expectedResolvedHttpsUrl = (rawValue, hostname) => {
    const authorityStart = rawValue.indexOf("//") + 2;
    const authorityEndOffset = rawValue.slice(authorityStart).search(/[/?#]/u);
    const suffix = authorityEndOffset === -1 ? "" : rawValue.slice(authorityStart + authorityEndOffset);
    const resolvedSuffix = suffix === "" ? "/" : suffix.startsWith("?") || suffix.startsWith("#") ? `/${suffix}` : suffix;
    return `https://${hostname}${resolvedSuffix}`;
  };

  const validateExternalUrl = (rawValue, { allowFragment = false } = {}) => {
    if (typeof rawValue !== "string" || rawValue === "" || rawValue.length > 2048) return null;
    if (rawValue.slice(0, 8).toLowerCase() !== "https://" || rawValue.includes("\\")) return null;
    if (hasRawControlOrWhitespace(rawValue) || hasEncodedControlOrWhitespace(rawValue)) return null;

    const decodedValues = recursivelyDecodedValues(rawValue);
    if (!decodedValues) return null;
    if (decodedValues.some((decoded) => (
      decoded.includes("\\")
      || ENCODED_URL_STRUCTURE_PATTERN.test(decoded)
      || !SAFE_URL_SUFFIX_PATTERN.test(decoded.slice(decoded.indexOf("//") + 2).replace(/^[^/?#]*/u, ""))
      || hasDotPathSegment(decoded)
    ))) return null;
    if (!allowFragment && decodedValues.some((decoded) => decoded.includes("#"))) return null;

    let parsed;
    try {
      parsed = new URL(rawValue);
    } catch (_error) {
      return null;
    }

    if (parsed.protocol !== "https:" || parsed.username !== "" || parsed.password !== "" || parsed.port !== "") return null;
    if (!allowFragment && parsed.hash !== "") return null;

    const authorityStart = rawValue.indexOf("//") + 2;
    const authority = rawValue.slice(authorityStart).split(/[/?#]/u, 1)[0];
    const suffix = rawValue.slice(authorityStart + authority.length);
    const hostname = parsed.hostname.toLowerCase();
    if (
      authority === ""
      || !SAFE_HOST_AUTHORITY_PATTERN.test(authority)
      || authority.includes("@")
      || authority.includes(":")
      || authority.toLowerCase() !== hostname
      || hostname.length > 253
      || hostname.startsWith(".")
      || hostname.endsWith(".")
      || hostname.includes("..")
      || !hostname.includes(".")
      || /^\d+(?:\.\d+){3}$/u.test(hostname)
      || !SAFE_URL_SUFFIX_PATTERN.test(suffix)
    ) return null;

    const labels = hostname.split(".");
    if (labels.some((label) => label.length === 0 || label.length > 63 || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u.test(label))) return null;

    const comparableHref = expectedResolvedHttpsUrl(rawValue, hostname);
    if (parsed.href !== comparableHref) return null;

    return Object.freeze({ href: rawValue, comparableHref, url: parsed });
  };

  const safeBoundedText = (value, maximum) => {
    const text = typeof value === "string" ? value.trim() : "";
    if (text === "" || text.length > maximum || hasRawControlOrWhitespace(text.replaceAll(" ", ""))) return null;
    return text;
  };

  const decodePublicFragment = (hash) => {
    if (typeof hash !== "string" || !hash.startsWith("#") || hash.length > 196) return null;
    const decodedValues = recursivelyDecodedValues(hash);
    if (!decodedValues) return null;
    const decoded = decodedValues.at(-1);
    if (!decoded.startsWith("#") || decoded.indexOf("#", 1) !== -1) return null;
    const publicId = decoded.slice(1);
    return SAFE_PUBLIC_ID_PATTERN.test(publicId) ? publicId : null;
  };

  const safeBaseHref = () => {
    if (/^https?:$/u.test(window.location.protocol)) return window.location.href;
    try {
      const base = new URL(document.baseURI);
      if (/^https?:$/u.test(base.protocol)) return base.href;
    } catch (_error) {
      // Fall through to a non-routable validation origin.
    }
    return "https://service.invalid/";
  };

  const currentDocumentUrl = (root) => {
    const rawCurrentUrl = root.dataset.guruServiceCurrentUrl || "";
    try {
      return new URL(rawCurrentUrl || safeBaseHref(), safeBaseHref());
    } catch (_error) {
      return new URL(safeBaseHref());
    }
  };

  const sameDocument = (left, right) => (
    left.protocol === right.protocol
    && left.hostname.toLowerCase() === right.hostname.toLowerCase()
    && left.port === right.port
    && left.pathname === right.pathname
    && left.search === right.search
  );

  const validateRootedUrl = (rawValue, baseUrl) => {
    if (!rawValue.startsWith("/") || rawValue.startsWith("//") || rawValue.length > 2048) return null;
    if (!SAFE_URL_SUFFIX_PATTERN.test(rawValue) || hasRawControlOrWhitespace(rawValue) || hasEncodedControlOrWhitespace(rawValue)) return null;
    const decodedValues = recursivelyDecodedValues(rawValue);
    if (!decodedValues || decodedValues.some((decoded) => (
      decoded.includes("\\")
      || ENCODED_URL_STRUCTURE_PATTERN.test(decoded)
      || !SAFE_URL_SUFFIX_PATTERN.test(decoded)
    ))) return null;

    for (const decoded of decodedValues) {
      const path = decoded.split(/[?#]/u, 1)[0];
      if (path.split("/").some((segment) => segment === "." || segment === "..")) return null;
    }

    let parsed;
    try {
      parsed = new URL(rawValue, baseUrl);
    } catch (_error) {
      return null;
    }
    if (`${parsed.pathname}${parsed.search}${parsed.hash}` !== rawValue) return null;
    return parsed;
  };

  const validateAuthoredHref = (rawValue, root, publicTargets) => {
    if (typeof rawValue !== "string" || rawValue === "" || rawValue.length > 2048) return null;
    if (hasRawControlOrWhitespace(rawValue) || hasEncodedControlOrWhitespace(rawValue) || rawValue.includes("\\")) return null;

    const currentUrl = currentDocumentUrl(root);
    if (rawValue.startsWith("#")) {
      const publicId = decodePublicFragment(rawValue);
      if (!publicId || !publicTargets.has(publicId)) return null;
      const parsed = new URL(rawValue, currentUrl);
      return Object.freeze({ href: rawValue, comparableHref: parsed.href, samePagePublicId: publicId, external: false });
    }

    let parsed;
    let external = false;
    if (rawValue.startsWith("/")) {
      parsed = validateRootedUrl(rawValue, currentUrl);
    } else {
      const validated = validateExternalUrl(rawValue, { allowFragment: true });
      parsed = validated?.url || null;
      external = Boolean(parsed && parsed.origin !== currentUrl.origin);
    }
    if (!parsed) return null;

    const publicId = parsed.hash === "" ? null : decodePublicFragment(parsed.hash);
    if (parsed.hash !== "" && !publicId) return null;
    if (sameDocument(parsed, currentUrl) && publicId && !publicTargets.has(publicId)) return null;

    return Object.freeze({
      href: rawValue,
      comparableHref: parsed.href,
      samePagePublicId: sameDocument(parsed, currentUrl) ? publicId : null,
      external,
    });
  };

  const styleSuppresses = (element) => {
    const style = element.style;
    const opacity = Number.parseFloat(style.opacity || "1");
    const rawStyle = element.getAttribute("style") || "";
    return style.display === "none"
      || ["hidden", "collapse"].includes(style.visibility)
      || style.contentVisibility === "hidden"
      || (Number.isFinite(opacity) && opacity <= 0)
      || /(?:filter\s*:\s*opacity\(\s*0(?:\.0+)?\s*\)|transform\s*:\s*scale\(\s*0(?:\.0+)?\s*\))/iu.test(rawStyle);
  };

  const sourceElementIsSuppressed = (element) => {
    if (element.hasAttribute("hidden") || element.hasAttribute("inert")) return true;
    if (element.getAttribute("aria-hidden")?.trim().toLowerCase() === "true") return true;
    if ([...element.classList].some((className) => HIDDEN_CLASS_PATTERN.test(className))) return true;
    return styleSuppresses(element);
  };

  const sourceTreeIsSuppressed = (element, boundary) => {
    let current = element;
    while (current) {
      if (sourceElementIsSuppressed(current)) return true;
      if (current === boundary) break;
      current = current.parentElement;
    }
    return false;
  };

  const usableText = (element, maximum = Number.POSITIVE_INFINITY) => {
    const text = (element?.textContent || "").trim();
    return text !== "" && text.length <= maximum ? text : null;
  };

  const elementHasHumanAuthority = (element) => {
    const attributes = ["title", "aria-label", "alt", "value", "placeholder"];
    if (element.localName.toLowerCase() === "track") attributes.push("label");
    return attributes.some((attribute) => (
      element.hasAttribute(attribute) && (element.getAttribute(attribute) || "").trim() !== ""
    ));
  };

  const sanitizeSource = (rawSource, root) => {
    if (typeof rawSource !== "string" || rawSource.trim() === "" || rawSource.length > MAX_SOURCE_CHARACTERS) return null;
    if (RAW_FRAME_ELEMENT_PATTERN.test(rawSource)) return null;
    if (RAW_PARSER_CONTAINER_PATTERN.test(rawSource)) return null;
    if (rawSource.includes(ENTITY_REFERENCE_PROBE_TOKEN)) return null;

    const parsed = new DOMParser().parseFromString(rawSource, "text/html");
    const entityProbeSource = rawSource.replace(ENTITY_REFERENCE_AMPERSAND_PATTERN, ENTITY_REFERENCE_PROBE_TOKEN);
    const entityProbe = new DOMParser().parseFromString(entityProbeSource, "text/html");
    const sourceAnchors = [...parsed.body.querySelectorAll("a")];
    const entityProbeAnchors = [...entityProbe.body.querySelectorAll("a")];
    if (sourceAnchors.length !== entityProbeAnchors.length) return null;
    const anchorsWithEntityHref = new WeakSet();
    sourceAnchors.forEach((anchor, index) => {
      const parsedHref = anchor.getAttribute("href") || "";
      const probeHref = entityProbeAnchors[index].getAttribute("href") || "";
      const restoredProbeHref = probeHref.replaceAll(ENTITY_REFERENCE_PROBE_TOKEN, "&");
      if (probeHref.includes(ENTITY_REFERENCE_PROBE_TOKEN) && restoredProbeHref !== parsedHref) {
        anchorsWithEntityHref.add(anchor);
      }
    });
    const parsedElements = [...parsed.querySelectorAll("*")].filter((element) => !["html", "head", "body"].includes(element.localName.toLowerCase()));
    const sourceElements = [...parsed.body.querySelectorAll("*")];
    const parserContainers = [parsed.documentElement, parsed.head, parsed.body];
    const parserContainerHasAuthority = parserContainers.some((element) => (
      elementHasHumanAuthority(element)
    ));
    // DOMParser creates bare html/head/body containers. Any container attribute
    // is therefore authored metadata that this fragment reconstruction cannot preserve.
    const parserContainerHasForbiddenMetadata = parserContainers.some((element) => element.attributes.length > 0);
    const outsideBodyHasReadableAuthority = parsedElements.some((element) => (
      !parsed.body.contains(element)
      && !sourceTreeIsSuppressed(element, null)
      && (elementHasHumanAuthority(element) || (element.textContent || "").trim() !== "")
    ));
    if (
      parsedElements.length > MAX_SOURCE_NODES
      || parserContainerHasAuthority
      || parserContainerHasForbiddenMetadata
      || outsideBodyHasReadableAuthority
      || parsedElements.some((element) => (
        element.localName.toLowerCase() === "style"
        || element.hasAttribute("style")
        || [...element.classList].some((className) => !SAFE_AUTHORED_CLASSES.has(className))
      ))
    ) return null;

    const rawSectionToken = (root.dataset.sectionId || "section").replace(/[^A-Za-z0-9_-]/gu, "-").slice(0, 80) || "section";
    const matchingRoots = [...document.querySelectorAll(ROOT_SELECTOR)].filter((candidate) => (
      (candidate.dataset.sectionId || "section").replace(/[^A-Za-z0-9_-]/gu, "-").slice(0, 80) === rawSectionToken
    ));
    const rootOrdinal = Math.max(1, matchingRoots.indexOf(root) + 1);
    const sectionToken = matchingRoots.length > 1 ? `${rawSectionToken}-${rootOrdinal}` : rawSectionToken;
    const sourceIdEntries = new Map();
    const elementIdMap = new WeakMap();
    const usedIds = new Set();

    sourceElements.forEach((element, index) => {
      const tagName = element.localName.toLowerCase();
      const sourceId = element.getAttribute("id") || "";
      if (!SAFE_PUBLIC_ID_PATTERN.test(sourceId)) return;

      const entries = sourceIdEntries.get(sourceId) || [];
      entries.push(element);
      sourceIdEntries.set(sourceId, entries);
      if (!ALLOWED_ELEMENTS.has(tagName) || sourceTreeIsSuppressed(element, parsed.body)) return;

      const occurrence = entries.length;
      const baseId = `GuruServiceContent-${sectionToken}-${sourceId}${occurrence > 1 ? `-${occurrence}` : ""}`;
      let safeId = baseId;
      let collision = index + 2;
      while (usedIds.has(safeId) || document.getElementById(safeId)) {
        safeId = `${baseId}-${collision}`;
        collision += 1;
      }
      usedIds.add(safeId);
      elementIdMap.set(element, safeId);
    });

    let visitedNodes = 0;
    let copiedCharacters = 0;
    let conservationFailed = false;
    const authorityRegistry = new Map();
    const removableAuthorityContainers = new Set();
    const targetForSource = new WeakMap();
    const sourceForTarget = new WeakMap();
    const pendingHeadings = [];
    const pendingDetails = [];
    const pendingAnchors = [];
    const pendingHeaderRelations = [];

    const appendText = (targetParent, text) => {
      copiedCharacters += text.length;
      if (copiedCharacters > MAX_SOURCE_CHARACTERS) throw new RangeError("Service source text exceeds its enhancement boundary");
      targetParent.append(document.createTextNode(text));
    };

    const appendNeutralText = (targetParent, value) => {
      const text = safeBoundedText(value || "", 1000);
      if (!text) return false;
      if (!targetParent.lastChild || !/\s$/u.test(targetParent.textContent || "")) appendText(targetParent, " ");
      const neutral = document.createElement("span");
      neutral.className = "guru-service-neutralized-text";
      appendText(neutral, text);
      targetParent.append(neutral);
      appendText(targetParent, " ");
      return true;
    };

    const readableAlternative = (value, { allowLineBreaks = false } = {}) => {
      if (typeof value !== "string" || value.trim() === "") return Object.freeze({ present: false, text: null });
      const allowedWhitespace = allowLineBreaks ? /[ \t\n\r]/gu : / /gu;
      if (value.length > 1000 || hasRawControlOrWhitespace(value.replace(allowedWhitespace, ""))) {
        return Object.freeze({ present: true, text: null });
      }
      const normalized = value.replace(/[ \t\n\r]+/gu, " ").trim().normalize("NFC");
      const text = safeBoundedText(normalized, 1000);
      return Object.freeze({ present: true, text });
    };

    const decodePlainFallbackText = (value) => {
      if (typeof value !== "string" || /[<>]/u.test(value)) return null;
      const fallbackDocument = new DOMParser().parseFromString(value, "text/html");
      return fallbackDocument.body.textContent || "";
    };

    const readableTokens = (value) => String(value || "").normalize("NFC").match(/[\p{L}\p{N}_]+|[^\p{White_Space}]/gu) || [];

    const registerAuthority = (text) => {
      const existing = authorityRegistry.get(text);
      if (existing) return existing;
      if (authorityRegistry.size >= MAX_AUTHORITY_VALUES) {
        conservationFailed = true;
        return null;
      }
      const tokens = readableTokens(text);
      if (tokens.length === 0) {
        conservationFailed = true;
        return null;
      }
      const entry = Object.freeze({ text, tokens, placements: new Set(), order: authorityRegistry.size });
      authorityRegistry.set(text, entry);
      return entry;
    };

    const registerAuthorityPlacement = (text, target) => {
      const entry = registerAuthority(text);
      if (!entry) return false;
      entry.placements.add(target);
      return true;
    };

    const collectReadableAuthorities = (sourceNode) => {
      const attributes = ["title", "aria-label"];
      ["alt", "value", "placeholder"].forEach((attribute) => {
        if (sourceNode.hasAttribute(attribute)) attributes.push(attribute);
      });
      if (sourceNode.localName.toLowerCase() === "track" && sourceNode.hasAttribute("label")) attributes.push("label");

      const authorities = [];
      for (const attribute of attributes) {
        const rawValue = sourceNode.getAttribute(attribute) || "";
        const alternative = readableAlternative(rawValue);
        if (!alternative.present) continue;
        if (!alternative.text) {
          conservationFailed = true;
          return null;
        }
        if (!authorities.includes(alternative.text)) {
          if (!registerAuthority(alternative.text)) return null;
          authorities.push(alternative.text);
        }
      }
      return authorities;
    };

    const queueAuthorityPlacements = (target, authorities) => {
      for (const authority of authorities) {
        const entry = authorityRegistry.get(authority);
        if (!entry) conservationFailed = true;
        else entry.placements.add(target);
      }
    };

    const appendQueuedNeutral = (targetParent, neutral, authorities) => {
      queueAuthorityPlacements(neutral, authorities);
      if (neutral.childNodes.length === 0 && authorities.length === 0) return;
      targetParent.append(neutral);
      if (neutral.childNodes.length === 0) removableAuthorityContainers.add(neutral);
    };

    const countTokenSequence = (tokens, sequence) => {
      if (sequence.length === 0 || sequence.length > tokens.length) return 0;
      let count = 0;
      for (let index = 0; index <= tokens.length - sequence.length; index += 1) {
        let matches = true;
        for (let offset = 0; offset < sequence.length; offset += 1) {
          if (tokens[index + offset] !== sequence[offset]) {
            matches = false;
            break;
          }
        }
        if (matches) count += 1;
      }
      return count;
    };

    const renderedTextStream = (rootNode) => {
      let stream = "";
      const appendBoundary = () => {
        if (stream !== "" && !/\p{White_Space}$/u.test(stream)) stream += " ";
      };
      const visit = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          stream += node.nodeValue || "";
          return;
        }
        if (!(node instanceof Element) && !(node instanceof DocumentFragment)) return;
        const tagName = node instanceof Element ? node.localName.toLowerCase() : "";
        const hasBlockBoundary = node instanceof Element && (VOID_ELEMENTS.has(tagName) || !PHRASING_ELEMENTS.has(tagName));
        if (hasBlockBoundary) appendBoundary();
        node.childNodes.forEach(visit);
        if (hasBlockBoundary) appendBoundary();
      };
      visit(rootNode);
      return stream.replace(/\p{White_Space}+/gu, " ").trim().normalize("NFC");
    };

    const outputTokens = (rootNode) => readableTokens(renderedTextStream(rootNode));

    const resolveAuthorityRegistry = (fragment) => {
      const entries = [...authorityRegistry.values()].sort((left, right) => (
        right.tokens.length - left.tokens.length
        || right.text.length - left.text.length
        || left.order - right.order
      ));
      for (const entry of entries) {
        let represented = countTokenSequence(outputTokens(fragment), entry.tokens);
        if (represented > 1) return false;
        if (represented === 1) continue;
        const placement = [...entry.placements].find((candidate) => fragment.contains(candidate));
        if (!placement || !appendNeutralText(placement, entry.text)) return false;
        represented = countTokenSequence(outputTokens(fragment), entry.tokens);
        if (represented !== 1) return false;
      }

      removableAuthorityContainers.forEach((container) => {
        if (container.childNodes.length === 0) container.remove();
      });
      const finalTokens = outputTokens(fragment);
      const invalidEntry = entries.find((entry) => countTokenSequence(finalTokens, entry.tokens) !== 1);
      return !invalidEntry;
    };

    const copyChildren = (sourceParent, targetParent, depth) => {
      if (depth > MAX_SOURCE_DEPTH) throw new RangeError("Service source nesting exceeds its enhancement boundary");

      sourceParent.childNodes.forEach((sourceNode) => {
        visitedNodes += 1;
        if (visitedNodes > MAX_SOURCE_NODES) throw new RangeError("Service source node count exceeds its enhancement boundary");

        if (sourceNode.nodeType === Node.TEXT_NODE) {
          appendText(targetParent, sourceNode.nodeValue || "");
          return;
        }
        if (!(sourceNode instanceof Element)) return;
        if (sourceElementIsSuppressed(sourceNode)) return;

        const tagName = sourceNode.localName.toLowerCase();
        const authorities = collectReadableAuthorities(sourceNode);
        if (!authorities) return;

        if (["source", "track"].includes(tagName)) {
          const neutral = document.createElement("span");
          neutral.className = "guru-service-neutralized-text";
          appendQueuedNeutral(targetParent, neutral, authorities);
          return;
        }
        if (NON_CONTENT_ELEMENTS.has(tagName)) {
          if (authorities.length > 0) conservationFailed = true;
          return;
        }

        if (["img", "video", "audio", "object", "iframe", "embed"].includes(tagName)) {
          const neutral = document.createElement("span");
          neutral.className = "guru-service-neutralized-text";
          if (["img", "embed"].includes(tagName)) {
            appendQueuedNeutral(targetParent, neutral, authorities);
            return;
          }
          if (tagName === "iframe") {
            const fallbackText = sourceNode.textContent || "";
            if (fallbackText.length > 1000) {
              conservationFailed = true;
              return;
            }
            const decodedFallbackText = decodePlainFallbackText(fallbackText);
            if (decodedFallbackText === null) {
              conservationFailed = true;
              return;
            }
            const alternative = readableAlternative(decodedFallbackText, { allowLineBreaks: true });
            if (alternative.present && !alternative.text) conservationFailed = true;
            else if (alternative.text && (!registerAuthorityPlacement(alternative.text, neutral) || !appendNeutralText(neutral, alternative.text))) conservationFailed = true;
            appendQueuedNeutral(targetParent, neutral, authorities);
            return;
          }
          copyChildren(sourceNode, neutral, depth + 1);
          const fallback = readableAlternative(renderedTextStream(neutral), { allowLineBreaks: true });
          if (fallback.present && (!fallback.text || !registerAuthorityPlacement(fallback.text, neutral))) conservationFailed = true;
          appendQueuedNeutral(targetParent, neutral, authorities);
          return;
        }
        if (tagName === "input") {
          const type = (sourceNode.getAttribute("type") || "text").toLowerCase();
          if (type === "hidden") {
            if (authorities.length > 0) conservationFailed = true;
          } else {
            const neutral = document.createElement("span");
            neutral.className = "guru-service-neutralized-text";
            appendQueuedNeutral(targetParent, neutral, authorities);
          }
          return;
        }
        if (tagName === "textarea") {
          const neutral = document.createElement("span");
          neutral.className = "guru-service-neutralized-text";
          const fallback = readableAlternative(sourceNode.textContent || "", { allowLineBreaks: true });
          if (fallback.present && !fallback.text) conservationFailed = true;
          else if (fallback.text && (!registerAuthorityPlacement(fallback.text, neutral) || !appendNeutralText(neutral, fallback.text))) conservationFailed = true;
          appendQueuedNeutral(targetParent, neutral, authorities);
          return;
        }

        if (!ALLOWED_ELEMENTS.has(tagName)) {
          const neutral = document.createElement(INLINE_NEUTRAL_ELEMENTS.has(tagName) ? "span" : "div");
          neutral.className = INLINE_NEUTRAL_ELEMENTS.has(tagName)
            ? "guru-service-neutralized-text"
            : "guru-service-neutralized-block";
          const charactersBefore = copiedCharacters;
          copyChildren(sourceNode, neutral, depth + 1);
          queueAuthorityPlacements(neutral, authorities);
          const readableSource = (sourceNode.textContent || "").trim();
          if (readableSource !== "" && copiedCharacters === charactersBefore) conservationFailed = true;
          if (neutral.childNodes.length > 0 || authorities.length > 0) {
            targetParent.append(neutral);
            if (neutral.childNodes.length === 0) removableAuthorityContainers.add(neutral);
          }
          return;
        }

        const sourceParentTag = sourceNode.parentElement?.localName.toLowerCase();
        const targetTag = tagName === "summary" && sourceParentTag !== "details" ? "p" : tagName;
        const target = document.createElement(targetTag);
        const safeId = elementIdMap.get(sourceNode);
        if (safeId) target.id = safeId;
        targetForSource.set(sourceNode, target);
        sourceForTarget.set(target, sourceNode);

        if (tagName === "a") {
          pendingAnchors.push({
            source: sourceNode,
            target,
            rawHref: sourceNode.getAttribute("href") || "",
            hrefHasEntity: anchorsWithEntityHref.has(sourceNode),
          });
        } else if (tagName === "details") {
          if (sourceNode.hasAttribute("open")) target.open = true;
          pendingDetails.push({ source: sourceNode, target });
        } else if (["h2", "h3", "h4"].includes(tagName)) {
          pendingHeadings.push({ source: sourceNode, target });
        } else if (tagName === "th") {
          const scope = sourceNode.getAttribute("scope")?.toLowerCase();
          if (["col", "colgroup", "row", "rowgroup"].includes(scope)) target.setAttribute("scope", scope);
        } else if (tagName === "ol") {
          const start = Number.parseInt(sourceNode.getAttribute("start") || "", 10);
          if (Number.isInteger(start) && start >= -10000 && start <= 10000) target.setAttribute("start", String(start));
        }

        if (tagName === "td" || tagName === "th") {
          ["colspan", "rowspan"].forEach((attribute) => {
            const span = Number.parseInt(sourceNode.getAttribute(attribute) || "", 10);
            if (Number.isInteger(span) && span >= 1 && span <= 100) target.setAttribute(attribute, String(span));
          });
          pendingHeaderRelations.push({ source: sourceNode, target });
        }

        targetParent.append(target);
        if (!VOID_ELEMENTS.has(tagName)) copyChildren(sourceNode, target, depth + 1);
        if (["ol", "ul", "dl", "table", "thead", "tbody", "tfoot", "tr"].includes(tagName)) {
          // These strict content models cannot accept an injected neutral child.
        } else if (VOID_ELEMENTS.has(tagName)) {
          const neutral = document.createElement("span");
          neutral.className = "guru-service-neutralized-text";
          queueAuthorityPlacements(neutral, authorities);
          if (authorities.length > 0) {
            target.after(neutral);
            removableAuthorityContainers.add(neutral);
          }
        } else {
          queueAuthorityPlacements(target, authorities);
        }
      });
    };

    const fragment = document.createDocumentFragment();
    try {
      copyChildren(parsed.body, fragment, 0);
    } catch (_error) {
      return null;
    }
    if (conservationFailed) return null;
    try {
      if (!resolveAuthorityRegistry(fragment)) return null;
    } catch (_error) {
      return null;
    }

    for (const { source, target } of pendingHeadings) {
      if (!fragment.contains(target)) continue;
      if (target.querySelector(HEADING_FORBIDDEN_DESCENDANTS)) return null;
      if (usableText(target)) continue;
      const parent = target.parentNode;
      if (!parent) continue;
      if (target.childNodes.length > 0) return null;
      while (target.firstChild) parent.insertBefore(target.firstChild, target);
      target.remove();
      targetForSource.delete(source);
    }

    for (const { source, target } of pendingDetails.reverse()) {
      if (!fragment.contains(target)) continue;
      const directSummaries = [...target.children].filter((child) => child.tagName === "SUMMARY");
      const summary = directSummaries[0] || null;
      const bodyText = [...target.childNodes]
        .filter((node) => node !== summary)
        .map((node) => node.textContent || "")
        .join(" ")
        .trim();
      const firstContentNode = [...target.childNodes].find((node) => (
        node.nodeType !== Node.TEXT_NODE || (node.nodeValue || "").trim() !== ""
      )) || null;
      const valid = directSummaries.length === 1
        && firstContentNode === summary
        && Boolean(usableText(summary, MAX_SUMMARY_CHARACTERS))
        && !summary.querySelector(SUMMARY_FORBIDDEN_DESCENDANTS)
        && bodyText !== ""
        && bodyText.length <= MAX_DISCLOSURE_CHARACTERS;
      if (valid) continue;

      const replacement = document.createElement("div");
      if (target.id) replacement.id = target.id;
      [...target.childNodes].forEach((child) => {
        if (child instanceof Element && child.tagName === "SUMMARY") {
          const hasForbiddenDescendant = Boolean(child.querySelector(SUMMARY_FORBIDDEN_DESCENDANTS));
          const summaryReplacement = document.createElement(hasForbiddenDescendant ? "div" : "p");
          if (child.id) summaryReplacement.id = child.id;
          const summaryContent = hasForbiddenDescendant ? summaryReplacement : document.createElement("strong");
          while (child.firstChild) summaryContent.append(child.firstChild);
          if (usableText(summaryContent)) {
            if (!hasForbiddenDescendant) summaryReplacement.append(summaryContent);
            replacement.append(summaryReplacement);
          }
          const summarySource = sourceForTarget.get(child);
          if (summarySource) targetForSource.set(summarySource, summaryReplacement);
        } else {
          replacement.append(child);
        }
      });
      target.replaceWith(replacement);
      targetForSource.set(source, replacement);
      sourceForTarget.set(replacement, source);
    }

    for (const { source, target } of pendingAnchors) {
      if (!fragment.contains(target) || usableText(target)) continue;
      if (target.childNodes.length > 0) return null;
      target.remove();
      targetForSource.delete(source);
    }

    const buildPublicTargets = () => {
      const publicTargets = new Map();
      sourceIdEntries.forEach((entries, sourceId) => {
        if (entries.length !== 1) return;
        const target = targetForSource.get(entries[0]);
        if (target instanceof Element && fragment.contains(target)) publicTargets.set(sourceId, target);
      });
      return publicTargets;
    };

    let publicTargets = buildPublicTargets();
    const samePageLinks = new Map();
    for (const { source, target, rawHref, hrefHasEntity } of pendingAnchors) {
      if (!fragment.contains(target)) continue;
      if (target.querySelector(ANCHOR_FORBIDDEN_DESCENDANTS)) return null;
      const destination = hrefHasEntity ? null : validateAuthoredHref(rawHref, root, publicTargets);
      if (!destination) {
        const replacement = document.createElement(target.querySelector(ANCHOR_BLOCK_DESCENDANTS) ? "div" : "span");
        if (target.id) replacement.id = target.id;
        while (target.firstChild) replacement.append(target.firstChild);
        target.replaceWith(replacement);
        targetForSource.set(source, replacement);
        sourceForTarget.set(replacement, source);
        continue;
      }

      target.setAttribute("href", destination.href);
      if (target.getAttribute("href") !== destination.href || target.href !== destination.comparableHref) return null;
      if (destination.external) target.setAttribute("rel", "external noopener noreferrer");
      if (destination.samePagePublicId) samePageLinks.set(target, destination.samePagePublicId);
    }

    publicTargets = buildPublicTargets();
    for (const { source, target } of pendingHeaderRelations) {
      if (!fragment.contains(target)) continue;
      const headers = (source.getAttribute("headers") || "").trim().split(/\s+/u).filter(Boolean);
      if (headers.length === 0) continue;
      if (headers.length > 32) return null;
      const rewritten = [];
      for (const publicId of headers) {
        const header = publicTargets.get(publicId);
        const headerSource = sourceIdEntries.get(publicId)?.[0] || null;
        if (
          !(header instanceof HTMLTableCellElement)
          || header.tagName !== "TH"
          || !header.id
          || !usableText(header)
          || headerSource?.closest("table") !== source.closest("table")
        ) return null;
        if (!rewritten.includes(header.id)) rewritten.push(header.id);
      }
      if (rewritten.length > 0) target.setAttribute("headers", rewritten.join(" "));
    }

    const hasOnlyChildren = (element, allowedElements, { allowText = true } = {}) => [...element.childNodes].every((child) => {
      if (child.nodeType === Node.TEXT_NODE) return allowText || (child.nodeValue || "").trim() === "";
      return child instanceof Element && allowedElements.has(child.localName.toLowerCase());
    });

    const validDefinitionList = (element) => {
      if (!hasOnlyChildren(element, new Set(["dt", "dd"]), { allowText: false })) return false;
      const children = [...element.children];
      if (children.length < 2 || children[0].localName.toLowerCase() !== "dt") return false;
      let definitionsInGroup = 0;
      let sawDefinition = false;
      for (const child of children) {
        const tagName = child.localName.toLowerCase();
        if (tagName === "dt") {
          if (definitionsInGroup > 0) definitionsInGroup = 0;
        } else {
          definitionsInGroup += 1;
          sawDefinition = true;
        }
      }
      return sawDefinition && definitionsInGroup > 0;
    };

    const validTable = (element) => {
      if (!hasOnlyChildren(element, new Set(["caption", "thead", "tbody", "tfoot"]), { allowText: false })) return false;
      const children = [...element.children].map((child) => child.localName.toLowerCase());
      let index = 0;
      if (children[index] === "caption") index += 1;
      if (children[index] === "thead") index += 1;
      let bodyGroups = 0;
      while (children[index] === "tbody") {
        bodyGroups += 1;
        index += 1;
      }
      if (children[index] === "tfoot") index += 1;
      return bodyGroups > 0 && index === children.length;
    };

    const validateOutputContentModels = () => {
      if (![...fragment.childNodes].every((child) => (
        child.nodeType === Node.TEXT_NODE
        || (child instanceof Element && FLOW_ELEMENTS.has(child.localName.toLowerCase()))
      ))) return false;

      for (const element of fragment.querySelectorAll("*")) {
        const tagName = element.localName.toLowerCase();
        const parentTag = element.parentElement?.localName.toLowerCase() || "";
        if (!ALLOWED_ELEMENTS.has(tagName)) return false;

        if (["li"].includes(tagName) && !["ol", "ul"].includes(parentTag)) return false;
        if (["dt", "dd"].includes(tagName) && parentTag !== "dl") return false;
        if (tagName === "summary" && parentTag !== "details") return false;
        if (tagName === "caption" && parentTag !== "table") return false;
        if (["thead", "tbody", "tfoot"].includes(tagName) && parentTag !== "table") return false;
        if (tagName === "tr" && !["thead", "tbody", "tfoot"].includes(parentTag)) return false;
        if (["td", "th"].includes(tagName) && parentTag !== "tr") return false;

        if (VOID_ELEMENTS.has(tagName) && element.childNodes.length > 0) return false;
        if (["a", "abbr", "b", "code", "em", "h2", "h3", "h4", "i", "p", "pre", "span", "strong", "summary", "u"].includes(tagName)) {
          if (!hasOnlyChildren(element, PHRASING_ELEMENTS)) return false;
          if (tagName === "a" && element.querySelector("a")) return false;
        } else if (["blockquote", "dd", "div", "dt", "li", "section", "td", "th"].includes(tagName)) {
          if (!hasOnlyChildren(element, FLOW_ELEMENTS)) return false;
          if (["dt", "th"].includes(tagName) && element.querySelector("h2,h3,h4,section")) return false;
        } else if (["ol", "ul"].includes(tagName)) {
          if (!hasOnlyChildren(element, new Set(["li"]), { allowText: false }) || element.children.length === 0) return false;
        } else if (tagName === "dl") {
          if (!validDefinitionList(element)) return false;
        } else if (tagName === "details") {
          const summaries = [...element.children].filter((child) => child.localName.toLowerCase() === "summary");
          const firstContentNode = [...element.childNodes].find((node) => (
            node.nodeType !== Node.TEXT_NODE || (node.nodeValue || "").trim() !== ""
          )) || null;
          if (
            summaries.length !== 1
            || firstContentNode !== summaries[0]
            || !hasOnlyChildren(element, new Set([...FLOW_ELEMENTS, "summary"]))
          ) return false;
        } else if (tagName === "table") {
          if (!validTable(element)) return false;
        } else if (["thead", "tbody", "tfoot"].includes(tagName)) {
          if (!hasOnlyChildren(element, new Set(["tr"]), { allowText: false }) || element.children.length === 0) return false;
        } else if (tagName === "tr") {
          if (!hasOnlyChildren(element, new Set(["td", "th"]), { allowText: false }) || element.children.length === 0) return false;
        } else if (tagName === "caption") {
          if (!hasOnlyChildren(element, FLOW_ELEMENTS) || element.querySelector("table")) return false;
        }
      }
      return true;
    };

    if (!validateOutputContentModels()) return null;

    if ((fragment.textContent || "").trim() === "" && !fragment.querySelector("hr")) return null;
    return Object.freeze({ fragment, publicTargets, samePageLinks });
  };

  const restoreContentFallback = (root) => {
    const content = root.querySelector("[data-guru-service-authoritative-content]");
    const fallback = root.querySelector("[data-guru-service-safe-fallback]");
    if (content?.dataset.guruServiceSanitized === "true") {
      content.replaceChildren();
      content.hidden = true;
      delete content.dataset.guruServiceSanitized;
    }
    if (fallback) fallback.hidden = false;
  };

  const mountSafeContent = (root, cleanups) => {
    const source = root.querySelector("template[data-guru-service-source]");
    const content = root.querySelector("[data-guru-service-authoritative-content]");
    const fallback = root.querySelector("[data-guru-service-safe-fallback]");
    if (!(source instanceof HTMLTemplateElement) || !content || !fallback) return null;

    const result = sanitizeSource(source.content.textContent || "", root);
    if (!result) return null;

    content.replaceChildren(result.fragment);
    content.hidden = false;
    content.dataset.guruServiceSanitized = "true";
    fallback.hidden = true;
    cleanups.push(() => restoreContentFallback(root));
    return result;
  };

  const mountFragmentNavigation = (root, sanitized, cleanups) => {
    if (!sanitized || sanitized.publicTargets.size === 0) return;
    const listeners = [];
    let frame = 0;

    const listen = (target, type, listener) => {
      target.addEventListener(type, listener);
      listeners.push(() => target.removeEventListener(type, listener));
    };

    const scrollPublicTarget = (hash = window.location.hash) => {
      const publicId = decodePublicFragment(hash);
      const target = publicId ? sanitized.publicTargets.get(publicId) : null;
      if (!target?.isConnected) return false;
      target.scrollIntoView({ block: "start", behavior: "auto" });
      return true;
    };

    listen(root, "click", (event) => {
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      const publicId = link ? sanitized.samePageLinks.get(link) : null;
      if (!link || !publicId || !root.contains(link)) return;

      event.preventDefault();
      const rawHref = link.getAttribute("href") || `#${publicId}`;
      const currentUrl = currentDocumentUrl(root);
      let nextUrl;
      try {
        nextUrl = new URL(rawHref, currentUrl);
      } catch (_error) {
        return;
      }

      try {
        const historyTarget = /^https?:$/u.test(window.location.protocol)
          ? `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
          : nextUrl.hash;
        if (window.location.hash === nextUrl.hash) window.history.replaceState(window.history.state, "", historyTarget);
        else window.history.pushState(window.history.state, "", historyTarget);
      } catch (_error) {
        // The public hash is a convenience; safe scrolling does not depend on history mutation.
      }
      scrollPublicTarget(`#${publicId}`);
    });
    listen(window, "hashchange", () => scrollPublicTarget());
    listen(window, "popstate", () => scrollPublicTarget());
    listen(window, "pageshow", () => scrollPublicTarget());

    frame = requestAnimationFrame(() => {
      frame = 0;
      scrollPublicTarget();
    });
    cleanups.push(() => {
      if (frame) cancelAnimationFrame(frame);
      listeners.splice(0).forEach((removeListener) => removeListener());
    });
  };

  const mountExternalLinks = (root, cleanups) => {
    root.querySelectorAll("[data-guru-service-related]").forEach((navigation) => {
      const list = navigation.querySelector("[data-guru-service-related-list]");
      if (!list) return;

      const initiallyHidden = navigation.hidden;
      const currentUrl = root.dataset.guruServiceCurrentUrl || "";
      let comparableCurrentUrl = null;
      try {
        comparableCurrentUrl = new URL(currentUrl, document.baseURI).href;
      } catch (_error) {
        comparableCurrentUrl = null;
      }

      list.querySelectorAll("template[data-guru-service-external-link]").forEach((candidate) => {
        if (!(candidate instanceof HTMLTemplateElement)) return;
        const label = safeBoundedText(candidate.dataset.label || "", MAX_EXTERNAL_LABEL_CHARACTERS);
        const destination = validateExternalUrl(candidate.dataset.url || "");
        if (!label || !destination || destination.comparableHref === comparableCurrentUrl) return;

        const link = document.createElement("a");
        link.className = "guru-service-related__link";
        if (candidate.dataset.hasImage === "true") link.classList.add("guru-service-related__link--image");
        link.setAttribute("href", destination.href);
        if (link.getAttribute("href") !== destination.href || link.href !== destination.comparableHref) return;
        link.setAttribute("rel", "external noopener noreferrer");
        link.dataset.guruServiceExternalOwned = "true";

        [...candidate.attributes].forEach((attribute) => {
          if (attribute.name.startsWith("data-shopify-")) link.setAttribute(attribute.name, attribute.value);
        });

        link.append(candidate.content.cloneNode(true));
        const title = document.createElement("span");
        title.className = "guru-service-related__title";
        title.textContent = label;
        const arrow = document.createElement("span");
        arrow.className = "guru-service-related__arrow";
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = "→";
        link.append(title, arrow);
        candidate.before(link);
        cleanups.push(() => link.remove());
      });

      navigation.hidden = list.querySelector("a[href]") === null;
      cleanups.push(() => {
        navigation.hidden = initiallyHidden;
      });
    });
  };

  const restoreAttribute = (element, name, value) => {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  };

  const wrapTables = (root, cleanups) => {
    const content = root.querySelector("[data-guru-service-authoritative-content]:not([hidden])");
    if (!content) return;

    const tables = [...content.querySelectorAll("table")];
    if (tables.length === 0) return;

    const fallbackLabel = root.dataset.guruServiceTableLabel || "Table";
    const fallbackState = {
      marker: content.getAttribute("data-guru-service-table-fallback"),
      tabIndex: content.getAttribute("tabindex"),
      role: content.getAttribute("role"),
      label: content.getAttribute("aria-label"),
    };

    if (tables.length > MAX_ENHANCED_TABLES) {
      content.setAttribute("data-guru-service-table-fallback", "true");
      content.setAttribute("tabindex", "0");
      content.setAttribute("role", "region");
      content.setAttribute("aria-label", fallbackLabel);
      cleanups.push(() => {
        restoreAttribute(content, "data-guru-service-table-fallback", fallbackState.marker);
        restoreAttribute(content, "tabindex", fallbackState.tabIndex);
        restoreAttribute(content, "role", fallbackState.role);
        restoreAttribute(content, "aria-label", fallbackState.label);
      });
      return;
    }

    tables.forEach((table, index) => {
      const caption = safeBoundedText(table.querySelector(":scope > caption")?.textContent || "", 120);
      const wrapper = document.createElement("div");
      wrapper.className = "guru-service-table-region";
      wrapper.dataset.guruServiceTableRegionOwned = "true";
      wrapper.tabIndex = 0;
      wrapper.setAttribute("role", "region");
      wrapper.setAttribute("aria-label", caption || `${fallbackLabel} ${index + 1}`);
      table.before(wrapper);
      wrapper.append(table);

      cleanups.push(() => {
        if (wrapper.contains(table)) wrapper.replaceWith(table);
        else wrapper.remove();
      });
    });

    content.dataset.guruServiceTablesEnhanced = "true";
    cleanups.push(() => {
      delete content.dataset.guruServiceTablesEnhanced;
      restoreAttribute(content, "data-guru-service-table-fallback", fallbackState.marker);
      restoreAttribute(content, "tabindex", fallbackState.tabIndex);
      restoreAttribute(content, "role", fallbackState.role);
      restoreAttribute(content, "aria-label", fallbackState.label);
    });
  };

  const renderedText = (element) => {
    if (!element || element.hidden || element.getClientRects().length === 0) return "";
    const style = getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return "";
    return (element.innerText || "").trim();
  };

  const usableDetailBodyText = (details, summary) => [...details.childNodes]
    .filter((node) => node !== summary)
    .map((node) => node.textContent || "")
    .join(" ")
    .trim();

  const usableFaqCandidate = (item) => {
    const directSummaries = [...item.children].filter((child) => child.tagName === "SUMMARY");
    const summary = directSummaries[0] || null;
    if (directSummaries.length !== 1 || item.firstElementChild !== summary || summary.querySelector(SUMMARY_FORBIDDEN_DESCENDANTS)) return null;

    const summaryText = renderedText(summary);
    if (summaryText === "" || summaryText.length > MAX_SUMMARY_CHARACTERS) return null;

    const bodyText = usableDetailBodyText(item, summary);
    if (bodyText === "" || bodyText.length > MAX_SEARCHABLE_CHARACTERS) return null;

    return { item, text: `${summaryText} ${bodyText}` };
  };

  const mountFaqSearch = (root, cleanups) => {
    const controls = root.querySelector("[data-guru-service-faq-controls]");
    const list = root.querySelector("[data-guru-service-faq-list]:not([hidden])");
    if (!controls || !list) return;

    const form = controls.querySelector("[data-guru-service-faq-search]");
    const input = controls.querySelector("[data-guru-service-faq-query]");
    const clear = controls.querySelector("[data-guru-service-faq-clear]");
    const status = controls.querySelector("[data-guru-service-faq-status]");
    const empty = root.querySelector("[data-guru-service-faq-empty]");
    if (!form || !input || !clear || !status || !empty) return;

    const topLevelDetails = [...list.querySelectorAll("details")].filter((item) => {
      const nestedInDetails = item.parentElement?.closest("details");
      return !nestedInDetails && !item.hidden && item.getClientRects().length > 0;
    });
    const candidates = topLevelDetails.map(usableFaqCandidate).filter(Boolean);
    if (candidates.length === 0 || candidates.length > MAX_FAQ_ITEMS || candidates.length !== topLevelDetails.length) return;

    const locale = ["da", "sv", "en"].includes(root.dataset.guruServiceLocale)
      ? root.dataset.guruServiceLocale
      : "en";
    const searchableItems = candidates.map(({ item, text }) => ({
      item,
      text: text.normalize("NFKC").toLocaleLowerCase(locale),
    }));
    const listeners = [];
    let active = true;

    const listen = (target, type, listener) => {
      target.addEventListener(type, listener);
      listeners.push(() => target.removeEventListener(type, listener));
    };
    const countMessage = (count) => {
      if (count === 0) return controls.dataset.countZero || "";
      if (count === 1) return controls.dataset.countOne || "";
      return (controls.dataset.countMany || "{count}").replace("{count}", String(count));
    };
    const deactivate = () => {
      if (!active) return;
      active = false;
      listeners.splice(0).forEach((removeListener) => removeListener());
      searchableItems.forEach(({ item }) => {
        if (item.dataset.guruServiceFiltered === "true") item.hidden = false;
        delete item.dataset.guruServiceFiltered;
      });
      input.value = "";
      controls.hidden = true;
      clear.hidden = true;
      empty.hidden = true;
      status.textContent = "";
      delete root.dataset.guruServiceFaqReady;
    };
    const update = () => {
      if (!active) return;
      try {
        const boundedValue = input.value.slice(0, MAX_QUERY_LENGTH);
        if (input.value !== boundedValue) input.value = boundedValue;
        const query = boundedValue.trim().normalize("NFKC").toLocaleLowerCase(locale);
        let visibleCount = 0;

        searchableItems.forEach(({ item, text }) => {
          const matches = query === "" || text.includes(query);
          if (matches) {
            if (item.dataset.guruServiceFiltered === "true") item.hidden = false;
            delete item.dataset.guruServiceFiltered;
            visibleCount += 1;
          } else {
            item.hidden = true;
            item.dataset.guruServiceFiltered = "true";
          }
        });

        clear.hidden = query === "";
        empty.hidden = query === "" || visibleCount !== 0;
        status.textContent = countMessage(visibleCount);
      } catch (_error) {
        deactivate();
      }
    };
    const clearSearch = () => {
      if (!active) return;
      input.value = "";
      update();
      input.focus();
    };

    listen(form, "submit", (event) => event.preventDefault());
    listen(input, "input", update);
    listen(input, "keydown", (event) => {
      if (event.key !== "Escape" || input.value === "") return;
      event.preventDefault();
      clearSearch();
    });
    listen(clear, "click", clearSearch);

    controls.hidden = false;
    root.dataset.guruServiceFaqReady = "true";
    update();
    cleanups.push(deactivate);
  };

  const failOpen = (root) => {
    try {
      const controls = root.querySelector("[data-guru-service-faq-controls]");
      const clear = root.querySelector("[data-guru-service-faq-clear]");
      const empty = root.querySelector("[data-guru-service-faq-empty]");
      const status = root.querySelector("[data-guru-service-faq-status]");
      const list = root.querySelector("[data-guru-service-faq-list]");

      if (controls) controls.hidden = true;
      if (clear) clear.hidden = true;
      if (empty) empty.hidden = true;
      if (status) status.textContent = "";
      list?.querySelectorAll("details[data-guru-service-filtered='true']").forEach((item) => {
        item.hidden = false;
        delete item.dataset.guruServiceFiltered;
      });

      root.querySelectorAll("[data-guru-service-table-region-owned='true']").forEach((wrapper) => {
        const table = wrapper.querySelector(":scope > table");
        if (table) wrapper.replaceWith(table);
        else wrapper.remove();
      });
      root.querySelectorAll("[data-guru-service-external-owned='true']").forEach((link) => link.remove());
      restoreContentFallback(root);
    } catch (_error) {
      // The passive escaped-source or explicit neutral server fallback remains the final boundary.
    }

    delete root.dataset.guruServiceFaqReady;
    delete root.dataset.guruServiceReady;
  };

  const mount = (root) => {
    const cleanups = [];
    let destroyed = false;

    try {
      const sanitized = mountSafeContent(root, cleanups);
      mountFragmentNavigation(root, sanitized, cleanups);
      mountExternalLinks(root, cleanups);
      wrapTables(root, cleanups);
      mountFaqSearch(root, cleanups);
      root.dataset.guruServiceReady = "true";
    } catch (_error) {
      cleanups.splice(0).reverse().forEach((cleanup) => {
        try {
          cleanup();
        } catch (_cleanupError) {
          // Continue toward the server fallback.
        }
      });
      failOpen(root);
      root.dataset.guruServiceReady = "failed-open";
    }

    return {
      destroy() {
        if (destroyed) return;
        destroyed = true;
        cleanups.splice(0).reverse().forEach((cleanup) => {
          try {
            cleanup();
          } catch (_error) {
            // Continue cleanup so one malformed enhancement cannot strand another.
          }
        });
        failOpen(root);
      },
    };
  };

  const scan = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, mount(root));
    });
  };

  const destroy = (scope) => {
    rootsWithin(scope).forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  };

  const runtime = Object.freeze({ version: 3, scan, destroy });
  window[RUNTIME_KEY] = runtime;

  document.addEventListener("shopify:section:load", (event) => scan(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
